import { useState } from "react";
import { C } from "../../../lib/tokens.js";
import { useUser } from "../../../lib/UserCacheContext.jsx";
import { IconButton, Button } from "../../../components/ui/Button.jsx";
import { Modal } from "../../../components/ui/Modal.jsx";
import { humanSize } from "./shared.jsx";
import { isImage, isPdf, isVideo, isAudio, dateLong, timeShort } from "../../../lib/utils.js";
import { usePdfThumbnail } from "../../../lib/pdfThumbnail.js";

function DeleteConfirmModal({ filename, onConfirm, onCancel }) {
  return (
    <Modal onClose={onCancel} width={420}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <div style={{
          width: 36, height: 36, borderRadius: "50%", background: "#FBE4E7", flex: "none",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <span className="material-icons" style={{ fontSize: 20, color: C.rust }}>delete_outline</span>
        </div>
        <div style={{ flex: 1, minWidth: 0, paddingTop: 4 }}>
          <div style={{ fontSize: 15, fontWeight: 500, color: C.fg }}>Delete this attachment?</div>
        </div>
        <button onClick={onCancel} style={{ border: "none", background: "none", cursor: "pointer", padding: 4, display: "flex", borderRadius: 4, flex: "none" }}>
          <span className="material-icons" style={{ fontSize: 18, color: C.iron }}>close</span>
        </button>
      </div>

      <div style={{
        display: "flex", alignItems: "center", gap: 8, padding: "8px 10px",
        background: C.platinum, border: `1px solid ${C.mercury}`, borderRadius: 4,
      }}>
        <span className="material-icons" style={{ fontSize: 16, color: C.iron, flex: "none" }}>description</span>
        <span style={{
          fontSize: 12, color: C.fg, fontWeight: 500, overflow: "hidden",
          textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {filename}
        </span>
      </div>

      <div style={{ fontSize: 12, color: C.rust }}>This can't be undone.</div>

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <Button kind="outline" onClick={onCancel}>Cancel</Button>
        <Button kind="danger" icon="delete" onClick={onConfirm}>Delete</Button>
      </div>
    </Modal>
  );
}

export function AttachmentItem({ a, onOpenLightbox }) {
  const author = useUser(a.by);
  const [progress, setProgress] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const downloading = progress !== null;
  const img = isImage(a.filename);
  const pdf = isPdf(a.filename);
  const video = isVideo(a.filename);
  const audio = isAudio(a.filename);
  const kind = img ? "image" : pdf ? "pdf" : video ? "video" : audio ? "audio" : null;
  const handleClick = kind ? () => onOpenLightbox(a.previewUrl, kind) : undefined;
  const pdfThumb = usePdfThumbnail(pdf ? a.blob : null, { width: 72 });

  async function handleDownload(e) {
    e.stopPropagation();
    if (downloading) return;
    setProgress(true);
    try {
      await a.onDownload?.(setProgress);
    } catch {
      // download failed — button re-enables below so the user can retry
    } finally {
      setProgress(null);
    }
  }

  if (a.failed) {
    return (
      <div style={{ padding: 12, border: "1px solid #ddd", borderRadius: 6 }}>
        <div>{a.filename}</div>
        <div style={{ color: "#999", fontSize: 12 }}>Failed to load attachment</div>
      </div>
    );
  }

  return (
    <div
      onClick={handleClick}
      style={{
        background: C.tin, border: `1px solid ${C.mercury}`, borderRadius: 6, padding: 12,
        display: "flex", alignItems: "center", gap: 10,
        cursor: kind ? "pointer" : "default",
      }}
    >
      <div
        style={{
          width: 36, height: 36, background: C.platinum, borderRadius: 4, overflow: "hidden",
          display: "flex", alignItems: "center", justifyContent: "center", flex: "none",
        }}
      >
        {img && a.previewUrl ? (
          <img src={a.previewUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        ) : pdf && pdfThumb ? (
          <img src={pdfThumb} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top", display: "block" }} />
        ) : (
          <span className="material-icons" style={{ fontSize: img ? 22 : 20, color: img ? C.silver : C.iron }}>
            {img ? "image" : pdf ? "picture_as_pdf" : video ? "movie" : audio ? "audiotrack" : "description"}
          </span>
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, color: C.fg, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {a.filename}
        </div>
        <div style={{ fontSize: 11, color: C.iron }}>
          {humanSize(a.size)}
          {author ? ` · ${author.name?.split(" ")[0]}` : ""}
          {a.createdAt ? (
            <>
              {" · "}
              <span style={{ whiteSpace: "nowrap" }}>
                {dateLong(a.createdAt)} {timeShort(a.createdAt)}
              </span>
            </>
          ) : ""}
        </div>
      </div>
      <IconButton
        icon="download"
        title={downloading ? "Downloading…" : "Download"}
        progress={downloading ? progress : undefined}
        disabled={downloading}
        onClick={handleDownload}
      />
      {a.onDelete ? (
        <IconButton
          icon="delete"
          title="Delete"
          hoverBg="#FCE8E8"
          onClick={(e) => {
            e.stopPropagation();
            setConfirmingDelete(true);
          }}
        />
      ) : null}
      {confirmingDelete && (
        <DeleteConfirmModal
          filename={a.filename}
          onCancel={() => setConfirmingDelete(false)}
          onConfirm={() => {
            setConfirmingDelete(false);
            a.onDelete?.();
          }}
        />
      )}
    </div>
  );
}